import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { HotTable } from "@handsontable/react";
import "handsontable/dist/handsontable.full.min.css";
import 'handsontable/styles/ht-theme-main.css';
import Sidebar from "./Sidebar";
import "./css/LiveFeedExcelView.css";
import { API_BASE_URL } from '../config';

import { Session } from "./utils/session";

const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};

export default function LiveFeedExcelView() {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const STATIC_HEADERS = [
    "SR NO.",
    "Today's Date",
    "DOC ID",
    "Patient Name",
    "Doctor Name",
    "Vendor",
    "Report type",
    "Report Subtype",
    "Status",
    "Gender",
    "DOB",
    "DOL",
    "Scribed By",
    "Upload",
    "QC",
    "Cheked in Portal",
    "Time In",
    "Case Type"
  ];

  const rows = 40;
  const cols = STATIC_HEADERS.length;

  const alpha = useCallback((n) => {
    let s = "";
    while (n >= 0) {
      s = String.fromCharCode((n % 26) + 65) + s;
      n = Math.floor(n / 26) - 1;
    }
    return s;
  }, []);

  const createEmptyGrid = useCallback((r, c) =>
    Array.from({ length: r }, () => Array.from({ length: c }, () => "")),
    []);

  const [data, setData] = useState(() => createEmptyGrid(rows, cols));
  const [colHeaders, setColHeaders] = useState(STATIC_HEADERS);

  const hotRef = useRef(null);
  const lastHeader = useRef({ row: null, col: null });

  const handleAfterOnCellMouseDown = useCallback((event, coords, TD) => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    if (coords && coords.row === -1 && coords.col >= 0) {
      const colIndex = coords.col;

      if (typeof lastHeader.current.row === 'number') {
        const r = lastHeader.current.row;
        lastHeader.current.row = null;
        lastHeader.current.col = null;
        setTimeout(() => hot.selectCell(r, colIndex), 0);
        return;
      }

      setTimeout(() => {
        if (typeof hot.selectColumns === 'function') {
          hot.selectColumns(colIndex, colIndex);
        } else {
          hot.selectCells([[0, colIndex, hot.countRows() - 1, colIndex]]);
        }
      }, 0);

      lastHeader.current.col = colIndex;
      lastHeader.current.row = null;
      return;
    }

    if (coords && coords.col === -1 && coords.row >= 0) {
      const rowIndex = coords.row;

      if (typeof lastHeader.current.col === 'number') {
        const c = lastHeader.current.col;
        lastHeader.current.row = null;
        lastHeader.current.col = null;
        setTimeout(() => hot.selectCell(rowIndex, c), 0);
        return;
      }

      setTimeout(() => {
        if (typeof hot.selectRows === 'function') {
          hot.selectRows(rowIndex, rowIndex);
        } else {
          hot.selectCells([[rowIndex, 0, rowIndex, hot.countCols() - 1]]);
        }
      }, 0);

      lastHeader.current.row = rowIndex;
      lastHeader.current.col = null;
      return;
    }

    if (coords && coords.row >= 0 && coords.col >= 0) {
      lastHeader.current.row = null;
      lastHeader.current.col = null;
    }
  }, []);


  const handleAfterChange = useCallback((changes, source) => {
    if (!changes) return;
    if (source === "loadData") return;
    setData((prev) => {
      const next = prev.map((r) => r.slice());
      for (const change of changes) {
        const [r, c, newVal] = change;
        if (typeof c === "string") continue;
        if (r >= 0 && r < next.length && c >= 0 && c < next[0].length) next[r][c] = newVal;
      }
      return next;
    });
  }, []);

  const syncTableWithState = useCallback((nextData) => {
    const hot = hotRef.current?.hotInstance;
    if (hot) hot.loadData(nextData);
  }, []);

  const ensureTableSize = useCallback((neededRows, neededCols) => {
    return new Promise((resolve) => {
      setData((prev) => {
        let next = prev.map((r) => r.slice());
        const currentRows = next.length;
        const currentCols = next[0]?.length || 0;
        if (neededRows > currentRows) {
          const rowsToAdd = neededRows - currentRows;
          for (let i = 0; i < rowsToAdd; i++) next.push(Array.from({ length: currentCols }, () => ""));
        }
        if (neededCols > currentCols) {
          const colsToAdd = neededCols - currentCols;
          next = next.map((r) => [...r, ...Array.from({ length: colsToAdd }, () => "")]);
          setColHeaders((prevH) => {
            const newH = [...prevH];
            const start = newH.length;
            for (let i = 0; i < colsToAdd; i++) newH.push(alpha(start + i));
            setTimeout(() => {
              const hot = hotRef.current?.hotInstance;
              if (hot) hot.updateSettings({ colHeaders: newH });
            }, 0);
            return newH;
          });
        }
        setTimeout(() => syncTableWithState(next), 0);
        resolve();
        return next;
      });
    });
  }, [alpha, syncTableWithState]);

  const addRow = useCallback(() => {
    setData((prev) => {
      const next = [...prev, Array.from({ length: prev[0]?.length || cols }, () => "")];
      setTimeout(() => syncTableWithState(next), 0);
      return next;
    });
  }, [cols, syncTableWithState]);

  const insertRow = useCallback((index = 0) => {
    setData((prev) => {
      const copy = prev.map((r) => r.slice());
      copy.splice(index, 0, Array.from({ length: copy[0]?.length || cols }, () => ""));
      setTimeout(() => syncTableWithState(copy), 0);
      return copy;
    });
  }, [cols, syncTableWithState]);

  const addColumn = useCallback(() => {
    setData((prev) => {
      const next = prev.map((r) => [...r, ""]);
      setTimeout(() => syncTableWithState(next), 0);
      return next;
    });
    setColHeaders((prev) => {
      const nextIndex = prev.length;
      const next = [...prev, alpha(nextIndex)];
      setTimeout(() => {
        const hot = hotRef.current?.hotInstance;
        if (hot) hot.updateSettings({ colHeaders: next });
      }, 0);
      return next;
    });
  }, [alpha, syncTableWithState]);

  const removeLastColumn = useCallback(() => {
    setData((prev) => {
      const next = prev.map((r) => r.slice(0, -1));
      setTimeout(() => syncTableWithState(next), 0);
      return next;
    });
    setColHeaders((prev) => {
      const next = prev.slice(0, -1);
      setTimeout(() => {
        const hot = hotRef.current?.hotInstance;
        if (hot) hot.updateSettings({ colHeaders: next });
      }, 0);
      return next;
    });
  }, [syncTableWithState]);
  const isBlank = useCallback(v => v === null || v === undefined || String(v).trim() === '', []);

  const colIndexToLetter = useCallback((index) => {
    let s = '';
    let n = index + 1;
    while (n > 0) {
      const rem = (n - 1) % 26;
      s = String.fromCharCode(65 + rem) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s;
  }, []);

  const saveToServer = useCallback(async () => {
    try {
      const hot = hotRef.current?.hotInstance;
      const latest = hot ? hot.getData() : data;
      const usedColumnIndexes = (latest[0] || []).map((_, i) => i)
        .filter(i => latest.some(row => !isBlank(row[i])));
      const excelData = latest
        .map((row, rowIndex) => {
          const obj = {};
          usedColumnIndexes.forEach(colIndex => {
            const cell = row[colIndex];
            if (!isBlank(cell)) {
              const colLetter = colIndexToLetter(colIndex);
              const key = `${colLetter}${rowIndex + 1}`;
              obj[key] = cell;
            }
          });
          return obj;
        })
        .filter(obj => Object.keys(obj).length > 0);
      const jsonExcelData = JSON.stringify(excelData, null, 2);
      console.log("----- JSON -----", jsonExcelData);
      const payload = { SheetData: jsonExcelData };
      const resp = await fetch(`${API_BASE_URL}/api/LiveFeedExcel/SaveExcelData`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      console.log("----- JSON -----", resp);
      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
      }
      alert("Saved successfully");
    } catch (err) {
      console.error(err);
      alert("Save error: " + err.message);
    }
  }, [data, isBlank, colIndexToLetter]);

  // Sidebar logic - useEffect for direct DOM manipulation
  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth <= 991) setShowMobileSidebar((s) => !s);
    else setHideDesktop((s) => !s);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) setShowMobileSidebar(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);


  const hotColumns = useMemo(() => colHeaders.map(() => ({})), [colHeaders]);

  const handleAfterOnCellDblClick = useCallback((row, col) => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    if (row < 0 || col < 0) return;
    hot.selectCell(row, col);
    setTimeout(() => {
      try {
        const editor = hot.getActiveEditor();
        if (editor && editor.beginEditing) editor.beginEditing();
      } catch (e) { }
    }, 50);
  }, []);

  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (!tag) return false;
      if (tag === "INPUT" || tag === "TEXTAREA") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const onKeyDown = async (e) => {
      if (isEditable(e.target)) return;
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      if (!(ctrl && (key === "c" || key === "v"))) return;

      // COPY
      if (key === "c") {
        e.preventDefault();
        try {
          const sel = hot.getSelected();
          if (!sel || !sel[0]) return;
          const [r1, c1, r2, c2] = sel[0];
          const sr = Math.min(r1, r2), er = Math.max(r1, r2);
          const sc = Math.min(c1, c2), ec = Math.max(c1, c2);
          const rowsToCopy = [];
          for (let rr = sr; rr <= er; rr++) {
            const cells = [];
            for (let cc = sc; cc <= ec; cc++) {
              const v = hot.getDataAtCell(rr, cc);
              cells.push(v == null ? "" : String(v));
            }
            rowsToCopy.push(cells.join("\t"));
          }
          const text = rowsToCopy.join("\n");
          if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(text);
          else {
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            ta.remove();
          }
        } catch (err) {
        }
        return;
      }

      // PASTE
      if (key === "v") {
        e.preventDefault();
        setIsLoading(true);
        let clip = "";
        try {
          if (navigator.clipboard && navigator.clipboard.readText) clip = await navigator.clipboard.readText();
        } catch (err) {
          clip = "";
        }
        if (!clip) return;


        try {
          const lines = clip.split(/\r\n|\n|\r/);
          const parsed = lines.map((ln) => ln.split("\t"));
          const maxCols = parsed.reduce((mx, r) => Math.max(mx, r.length), 0);

          const sel = hot.getSelected();
          let startR = 0, startC = 0;
          if (sel && sel[0]) {
            const [r1, c1, r2, c2] = sel[0];
            startR = Math.min(r1, r2);
            startC = Math.min(c1, c2);
          } else {
            const last = hot.getSelectedLast?.();
            if (last) { startR = last[0]; startC = last[1]; }
          }

          const neededRows = startR + parsed.length;
          const neededCols = startC + maxCols;
          await ensureTableSize(neededRows, neededCols);

          parsed.forEach((rowVals, rIdx) => {
            rowVals.forEach((cellValue, cIdx) => {
              const rr = startR + rIdx;
              const cc = startC + cIdx;
              const hotRows = hot.countRows();
              const hotCols = hot.countCols();
              if (rr >= 0 && rr < hotRows && cc >= 0 && cc < hotCols) {
                hot.setDataAtCell(rr, cc, cellValue);
              }
            });
          });

          setData(hot.getData());
        } catch (err) {
          console.error("Paste error:", err);
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const hot = hotRef.current?.hotInstance;
      if (hot) hot.loadData(data);
    }, 50);
  }, []);

  return (
    <div>
      {/* Overlay for mobile sidebar - always rendered, visibility controlled by useEffect */}
      <div id="sidebar-overlay" className="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />
      <div id="sidebar">
        <Sidebar />
      </div>
      <div id="content">
        <div className="d-flex align-items-center mb-3 gap-2">
          <button
            type="button"
            id="sidebarCollapse"
            className="btn btn-toggle"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <i className="fas fa-bars" />
          </button>

          <h2 className="excel-title">Live Feed (Editable)</h2>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={() => insertRow(0)}>Insert Row Top</button>
            <button className="btn btn-sm" onClick={addRow}>Add Row</button>
            <button className="btn btn-sm" onClick={addColumn}>Add Col</button>
            <button className="btn btn-sm" onClick={removeLastColumn}>Remove Col</button>
            <button className="btn btn-primary btn-sm" onClick={saveToServer}>Save</button>
          </div>
        </div>

        <div className="excel-sheet-wrapper page-card" style={{ position: "relative" }}>
          {isLoading && (
            <div className={"hf-loader-overlay block"}>
              <div className="hf-loader" />
            </div>
          )}
          <HotTable
            ref={hotRef}
            data={data}
            colHeaders={colHeaders}
            rowHeaders={true}
            columns={hotColumns}
            width="100%"
            height="calc(100vh - 160px)"
            stretchH="all"
            manualColumnResize={true}
            manualRowResize={true}
            contextMenu={true}
            copyPaste={true}
            selectionMode="range"
            multiSelect={false}
            undo={true}
            allowInsertRow={true}
            allowInsertColumn={true}
            allowRemoveRow={true}
            allowRemoveColumn={true}
            enterBeginsEditing={false}
            allowInvalid={true}
            afterChange={handleAfterChange}
            afterOnCellDblClick={handleAfterOnCellDblClick}
            afterOnCellMouseDown={handleAfterOnCellMouseDown}
            licenseKey="non-commercial-and-evaluation"
          />
        </div>
      </div>
    </div>
  );
}
